// src/features/catalog/lib/catalogFilters.ts
import type { CourseDifficulty } from '@/types/course'
import type { Lang, LangFilter } from '@/types/lang'

export interface CatalogFilterState {
  search: string
  categoriesFilter: number[]
  difficultyFilter: CourseDifficulty[]
  languageFilter: LangFilter
}

/**
 * True if the language filter is a "foreign" state the user must be told about:
 * not `'all'` (that's the explicit "no filter" command) AND not the interface
 * language (that's the silent default, never surfaced as a chip). A value that
 * arrived via a shared link (`?languageFilter=en` on a `ru` interface) satisfies
 * this — the user narrowed by language without knowing it, per the three-state
 * model in the catalog language-filter spec.
 */
export const isLanguageFilterActive = (
  languageFilter: LangFilter,
  interfaceLanguage: Lang,
): boolean => languageFilter !== 'all' && languageFilter !== interfaceLanguage

/**
 * True if there are filters that produce visible chips in ActiveFiltersBar
 * (search, categories, difficulty, and — since a "foreign" language filter got
 * its own removable chip — a languageFilter that differs from BOTH 'all' and
 * the interface language). The common (default-to-interface-language) case
 * still produces no chip: without this check, "Активно: [Сбросить всё]"
 * rendered empty on every catalog load (regression fixed in 8a44b88).
 *
 * Used by CatalogFilterBar to decide whether to show the "Активно: …" row.
 */
export const hasRemovableFilterChips = (f: CatalogFilterState, interfaceLanguage: Lang): boolean =>
  f.search.length > 0 ||
  f.categoriesFilter.length > 0 ||
  f.difficultyFilter.length > 0 ||
  isLanguageFilterActive(f.languageFilter, interfaceLanguage)

/**
 * True if any catalog filter affects the result set (search, categories, difficulty, OR language).
 * `languageFilter` counts too: it's ALWAYS sent to the backend (defaults to the interface
 * language, never omitted), so a catalog empty only because the interface language has no
 * courses yet is an active-filter empty state ("Ничего не найдено" + "Показать все языки"),
 * not "platform has zero courses" (`languageFilter === 'all'`, the actual "no filter" value).
 *
 * Used by Catalog/CatalogResults to pick the right empty state message.
 */
export const hasActiveCatalogFilters = (f: CatalogFilterState, interfaceLanguage: Lang): boolean =>
  hasRemovableFilterChips(f, interfaceLanguage) || f.languageFilter !== 'all'
